import { ChevronDown, ChevronLeft, ChevronRight, Layers, Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import type {
  DocumentIndexedData,
  LakeDocument,
  ParquetSection,
  TabularColumn,
  TabularQueryRequest,
} from '@/services/api/lake'
import { formatDocumentType, formatFileType } from '@/utils/document-labels'
import { ParquetPreview } from './ParquetPreview'
import { DetailCard, PageTitle, SectionHeading } from './sections'

interface IndexedDataTabProps {
  document: LakeDocument
  indexedData: DocumentIndexedData | null
  indexedLoading: boolean
  indexedError: string | null
  chunksPage: number
  expandedChunkIds: Set<string>
  onPageChange: (page: number) => void
  onToggleChunk: (chunkId: string) => void
  parquetSections: ParquetSection[]
  selectedSectionIndex: number
  onSelectSection: (index: number) => void
  onClearParquetSection: () => void
  parquetColumns: TabularColumn[]
  parquetRows: (string | number | boolean | null)[][]
  parquetTotalRows: number
  parquetFilteredRows: number
  parquetCurrentOffset: number
  parquetLoading: boolean
  parquetNavigating: boolean
  parquetError: string | null
  onParquetQuery: (req: TabularQueryRequest) => Promise<void>
  onParquetPrevPage: () => Promise<void>
  onParquetNextPage: () => Promise<void>
}

export function IndexedDataTab({
  document,
  indexedData,
  indexedLoading,
  indexedError,
  chunksPage,
  expandedChunkIds,
  onPageChange,
  onToggleChunk,
  parquetSections,
  selectedSectionIndex,
  onSelectSection,
  onClearParquetSection,
  parquetColumns,
  parquetRows,
  parquetTotalRows,
  parquetFilteredRows,
  parquetCurrentOffset,
  parquetLoading,
  parquetNavigating,
  parquetError,
  onParquetQuery,
  onParquetPrevPage,
  onParquetNextPage,
}: IndexedDataTabProps) {
  return (
    <div
      className="max-md:!px-4"
      style={{
        padding: '20px 24px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <PageTitle>Indexed Data</PageTitle>

      {document.has_parquet && (
        <DetailCard>
          <ParquetPreview
            parquetSections={parquetSections}
            selectedSectionIndex={selectedSectionIndex}
            onSelectSection={onSelectSection}
            onClearParquetSection={onClearParquetSection}
            parquetColumns={parquetColumns}
            parquetRows={parquetRows}
            parquetTotalRows={parquetTotalRows}
            parquetFilteredRows={parquetFilteredRows}
            parquetCurrentOffset={parquetCurrentOffset}
            parquetLoading={parquetLoading}
            parquetNavigating={parquetNavigating}
            parquetError={parquetError}
            onParquetQuery={onParquetQuery}
            onParquetPrevPage={onParquetPrevPage}
            onParquetNextPage={onParquetNextPage}
          />
        </DetailCard>
      )}

      {indexedLoading && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px 0',
          }}
        >
          <Loader2
            className="animate-spin"
            style={{ width: 18, height: 18, color: 'var(--muted-foreground)' }}
          />
        </div>
      )}

      {indexedError && (
        <div
          style={{
            padding: '10px 12px',
            borderRadius: '8px',
            border: '1px solid var(--destructive)',
            background: 'var(--destructive-surface)',
            color: 'var(--destructive)',
            fontSize: 12,
          }}
        >
          {indexedError}
        </div>
      )}

      {!indexedLoading && !indexedError && indexedData && (
        <>
          {/* Doc Store Section */}
          <DetailCard>
            <SectionHeading icon={<Layers size={14} />} label="Doc Store" />
            {indexedData.doc_record ? (
              <div className="rounded-lg bg-muted/40 border border-border p-3 space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <div className="text-[10px] text-muted-foreground italic">
                    Typesense document record
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-2 max-md:[&>div]:min-w-0 max-md:[&>div]:break-words md:grid-cols-2">
                  <div>
                    <span className="text-muted-foreground">ID:</span>{' '}
                    <span className="text-foreground font-mono text-[10px]">
                      {indexedData.doc_record.id}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Filename:</span>{' '}
                    <span className="text-foreground">{indexedData.doc_record.filename}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">File Type:</span>{' '}
                    <span className="text-foreground">
                      {formatFileType(indexedData.doc_record.file_type) || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Document Type:</span>{' '}
                    <span className="text-foreground">
                      {formatDocumentType(indexedData.doc_record.document_type) || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Label:</span>{' '}
                    <span className="text-foreground">{indexedData.doc_record.label || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Confidentiality:</span>{' '}
                    <span className="text-foreground">
                      {indexedData.doc_record.confidentiality || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Pages:</span>{' '}
                    <span className="text-foreground">
                      {indexedData.doc_record.page_count || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Rows:</span>{' '}
                    <span className="text-foreground">
                      {indexedData.doc_record.row_count || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Year/Month:</span>{' '}
                    <span className="text-foreground">
                      {indexedData.doc_record.document_year || 'N/A'}
                      {indexedData.doc_record.document_month
                        ? `/${indexedData.doc_record.document_month}`
                        : ''}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Has Parquet:</span>{' '}
                    <span className="text-foreground">
                      {indexedData.doc_record.has_parquet ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Imported at:</span>{' '}
                    <span className="text-foreground">
                      {indexedData.doc_record.imported_at
                        ? new Date(indexedData.doc_record.imported_at * 1000).toLocaleString()
                        : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Doc created:</span>{' '}
                    <span className="text-foreground">
                      {indexedData.doc_record.document_created_at
                        ? new Date(
                            indexedData.doc_record.document_created_at * 1000
                          ).toLocaleString()
                        : 'N/A'}
                    </span>
                  </div>
                  {indexedData.doc_record.exif_gps_latitude != null &&
                    indexedData.doc_record.exif_gps_longitude != null && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">GPS:</span>{' '}
                        <a
                          href={`https://www.google.com/maps?q=${indexedData.doc_record.exif_gps_latitude},${indexedData.doc_record.exif_gps_longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {indexedData.doc_record.exif_gps_latitude.toFixed(6)},{' '}
                          {indexedData.doc_record.exif_gps_longitude.toFixed(6)}
                        </a>
                      </div>
                    )}
                </div>
                <div className="pt-2 border-t border-border">
                  <div className="text-muted-foreground mb-1">Storage Path:</div>
                  <div className="text-muted-foreground font-mono text-[10px] break-all">
                    {indexedData.doc_record.storage_path}
                  </div>
                </div>
                {indexedData.doc_record.storage_prefixes &&
                  indexedData.doc_record.storage_prefixes.length > 0 && (
                    <div className="pt-2 border-t border-border">
                      <div className="text-muted-foreground mb-1">Storage Prefixes:</div>
                      <div className="flex flex-wrap gap-1">
                        {indexedData.doc_record.storage_prefixes.map((prefix, i) => (
                          <Badge key={i} variant="outline" className="text-[10px]">
                            {prefix}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                {indexedData.doc_record.file_summary && (
                  <div className="pt-2 border-t border-border">
                    <div className="text-muted-foreground mb-1">Summary:</div>
                    <p className="text-foreground leading-relaxed">
                      {indexedData.doc_record.file_summary}
                    </p>
                  </div>
                )}
                {indexedData.doc_record.full_text && (
                  <div className="pt-2 border-t border-border">
                    <div className="text-muted-foreground mb-1">
                      Full Text ({indexedData.doc_record.full_text.length} chars):
                    </div>
                    <p className="text-muted-foreground font-mono text-[10px] leading-relaxed line-clamp-3">
                      {indexedData.doc_record.full_text.slice(0, 500)}
                      {indexedData.doc_record.full_text.length > 500 && '...'}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--muted-foreground)',
                  fontStyle: 'italic',
                }}
              >
                Document not yet indexed to Typesense
              </div>
            )}
          </DetailCard>

          {/* Chunks Section */}
          <DetailCard>
            <SectionHeading
              icon={<Layers size={14} />}
              label="Chunks"
              count={indexedData.total_chunks}
            />
            {indexedData.chunks.length > 0 ? (
              <div className="space-y-2">
                {indexedData.chunks.map(chunk => {
                  const isExpanded = expandedChunkIds.has(chunk.id)
                  return (
                    <div
                      key={chunk.id}
                      className="rounded-lg bg-muted/50 border border-border p-3 hover:bg-muted/70 transition-colors cursor-pointer"
                      onClick={() => onToggleChunk(chunk.id)}
                    >
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                          )}
                          <Badge variant="outline" className="text-[10px]">
                            #{chunk.chunk_index}
                          </Badge>
                          {chunk.chunk_type && (
                            <Badge
                              variant="outline"
                              className="text-[10px] text-blue-700 dark:text-blue-400"
                            >
                              {chunk.chunk_type}
                            </Badge>
                          )}
                          {chunk.section && (
                            <span className="text-[10px] text-muted-foreground">
                              Section: {chunk.section}
                            </span>
                          )}
                          {chunk.page_number && (
                            <span className="text-[10px] text-muted-foreground">
                              p.{chunk.page_number}
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-foreground leading-relaxed line-clamp-2">
                        {chunk.content}
                      </p>

                      {/* Expanded content */}
                      {isExpanded && (
                        <div className="mt-4 space-y-3 border-t border-border pt-4">
                          <div>
                            <h5 className="text-xs font-medium text-muted-foreground mb-2">
                              Full Chunk Content
                            </h5>
                            <pre className="text-xs text-foreground bg-muted border border-border rounded p-3 whitespace-pre-wrap font-mono">
                              {chunk.content}
                            </pre>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-[10px]">
                            <div>
                              <span className="text-muted-foreground">Chunk ID:</span>
                              <div className="text-foreground font-mono truncate">{chunk.id}</div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Document ID:</span>
                              <div className="text-foreground font-mono truncate">
                                {chunk.document_id}
                              </div>
                            </div>
                            {chunk.row_index !== null && chunk.row_index !== undefined && (
                              <div>
                                <span className="text-muted-foreground">Row Index:</span>
                                <div className="text-foreground">{chunk.row_index}</div>
                              </div>
                            )}
                            {chunk.page_number && (
                              <div>
                                <span className="text-muted-foreground">Page:</span>
                                <div className="text-foreground">
                                  {chunk.page_number}
                                  {chunk.page_number_end &&
                                    chunk.page_number_end !== chunk.page_number &&
                                    `–${chunk.page_number_end}`}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--muted-foreground)',
                  fontStyle: 'italic',
                }}
              >
                No chunks found
              </div>
            )}

            {/* Pagination */}
            {indexedData.total_pages > 1 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: 8,
                  paddingTop: 12,
                  borderTop: '1px solid var(--border)',
                }}
              >
                <button
                  type="button"
                  onClick={() => onPageChange(chunksPage - 1)}
                  disabled={chunksPage === 1}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    fontSize: 11,
                    color: 'var(--foreground)',
                    cursor: chunksPage === 1 ? 'not-allowed' : 'pointer',
                    opacity: chunksPage === 1 ? 0.4 : 1,
                    fontFamily: 'inherit',
                  }}
                >
                  <ChevronLeft size={12} />
                  Previous
                </button>
                <span style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
                  Page {chunksPage} of {indexedData.total_pages}
                </span>
                <button
                  type="button"
                  onClick={() => onPageChange(chunksPage + 1)}
                  disabled={chunksPage === indexedData.total_pages}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    fontSize: 11,
                    color: 'var(--foreground)',
                    cursor: chunksPage === indexedData.total_pages ? 'not-allowed' : 'pointer',
                    opacity: chunksPage === indexedData.total_pages ? 0.4 : 1,
                    fontFamily: 'inherit',
                  }}
                >
                  Next
                  <ChevronRight size={12} />
                </button>
              </div>
            )}
          </DetailCard>
        </>
      )}
    </div>
  )
}
